import {
  createMessage,
  Handshake,
  type Message,
  MessageType,
  Queue,
  SDKState,
  Transport,
  type TransportConfig,
} from '@/core';

export interface BaseSDKConfig {
  allowedOrigins?: string[];
  targetOrigin?: string;
  debug?: boolean;
  /**
   * Receives every SDK log line when `debug=true`. Defaults to `console.log` if omitted.
   * Use this to surface SDK activity in a custom log panel (e.g. an admin Test page).
   */
  logger?: (...args: unknown[]) => void;
}

export type SDKHookFunction<TArgs extends unknown[] = unknown[], TReturn = unknown> = (...args: TArgs) => TReturn;

export interface BaseSDKHooks {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: SDKHookFunction<any, any> | undefined;
  onConnected?: SDKHookFunction<[], void>;
  onDisconnected?: SDKHookFunction<[], void>;
}

export abstract class BaseSDK<
  TConfig extends BaseSDKConfig = BaseSDKConfig,
  THooks extends BaseSDKHooks = BaseSDKHooks,
> {
  public abstract name: string;
  public state = new SDKState();

  protected config: TConfig;
  protected hooks: THooks;
  protected transport: Transport;
  protected queue: Queue;
  protected handshake: Handshake;

  protected constructor(
    config: TConfig,
    target: TransportConfig['targetWindow'],
    hooks: THooks,
    role: 'host' | 'game',
  ) {
    this.config = config;
    this.hooks = hooks;

    this.queue = new Queue();
    this.transport = new Transport({
      allowedOrigins: config.allowedOrigins?.length ? config.allowedOrigins : [window.location.origin],
      targetOrigin: config.targetOrigin ?? window.location.origin,
      targetWindow: target,
      debug: config.debug,
      logger: config.logger,
    });
    this.handshake = new Handshake(
      this.transport,
      { role, debug: config.debug, logger: config.logger },
      {
        onConnected: () => this.onHandshakeConnected(),
        onDisconnected: () => this.onHandshakeDisconnected(),
      },
    );

    this.transport.name = `Transport:${role}`;
    this.transport.subscribe((msg) => this.handleMessage(msg));
  }

  public init(): void {
    this.transport.listen();
    this.handshake.start();
    this.onInit();
    this.log('SDK initialized');
  }

  public destroy(): void {
    this.onDestroy();
    this.handshake.disconnect();
    this.transport.stop();
    this.log('SDK destroyed');
  }

  public isConnected(): boolean {
    return this.handshake.isConnected() && this.transport.isConnected();
  }

  protected sendOrQueue(message: Message): void {
    if (this.isConnected()) {
      this.transport.send(message);
    } else {
      this.queue.enqueue(message);
      this.log('Message queued (not connected yet)');
    }
  }

  protected async request<T>(
    requestType: MessageType,
    responseType: MessageType,
    payload: unknown = {},
    timeout = 5000,
  ): Promise<T> {
    const message = createMessage(requestType, payload);

    return new Promise<T>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let unsubscribe: (() => void) | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        unsubscribe?.();
      };

      unsubscribe = this.transport.subscribe((msg: Message) => {
        if (msg.type === responseType && msg.replyTo === message.id) {
          cleanup();
          resolve(msg.payload as T);
        }
      });

      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error(`Request timeout for ${requestType}`));
      }, timeout);

      this.sendOrQueue(message);
    });
  }

  protected log(...args: unknown[]): void {
    if (!this.config.debug) return;
    const tagged = [`[${this.name}]`, ...args];
    (this.config.logger ?? console.log)(...tagged);
  }

  protected onInit(): void {}

  protected onDestroy(): void {}

  protected onHandshakeConnected(): void {
    this.log('Handshake completed, connection established');

    const queuedMessages = this.queue.flush();
    this.log(`Flushing ${queuedMessages.length} queued messages`);
    queuedMessages.forEach((msg) => this.transport.send(msg));

    this.onConnected();

    if (typeof this.hooks.onConnected === 'function') {
      this.hooks.onConnected();
    }
  }

  protected onHandshakeDisconnected(): void {
    this.log('Handshake disconnected');
    this.onDisconnected();
    if (typeof this.hooks.onDisconnected === 'function') {
      this.hooks.onDisconnected();
    }
  }

  protected onConnected(): void {}

  protected onDisconnected(): void {}

  protected abstract handleMessage(message: Message): void;
}
